import { Box, Flex, Heading, Icon, Input, VStack } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { FormProvider, UseFormReturn } from "react-hook-form";
import { HiOutlineWrenchScrewdriver } from "react-icons/hi2";

import { Button, FormControl } from "../../../components";
import { useTheme } from "../../../components/ThemedChakraProvider/ThemedChakraProvider";
import { PositionListItemFragment } from "../../graphql";
import { useIsScreenPositionRequired } from "../../hooks/useIsScreenPositionRequired";
import { PositionField } from "./PositionField";
import { SetupFormValues } from "./types";

type AiInterviewerSetupSubStepProps = {
  methods: UseFormReturn<SetupFormValues>;
  onNext: () => void | Promise<void>;
  onCancel: () => void;
};

export const AiInterviewerSetupSubStep: React.FC<
  AiInterviewerSetupSubStepProps
> = ({ methods, onNext, onCancel }) => {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = methods;
  const theme = useTheme();
  const isPositionRequired = useIsScreenPositionRequired();
  const [selectedPosition, setSelectedPosition] = useState<
    PositionListItemFragment | undefined
  >();

  useEffect(() => {
    if (isPositionRequired && selectedPosition) {
      setValue("staticLinkEnabled", true);
    }
  }, [isPositionRequired, selectedPosition, setValue]);

  return (
    <FormProvider {...methods}>
      <Box w="100%" overflow="auto">
        <Flex
          direction="column"
          align="center"
          maxWidth="692px"
          p="12"
          my="8"
          mx="auto"
          gap="12"
          borderRadius="xl"
          boxShadow="base"
          border="1px solid"
          borderColor="blue.200"
          bg="white"
        >
          <Flex align="center" gap="2">
            <Icon
              as={HiOutlineWrenchScrewdriver}
              boxSize="6"
              color="gray.700"
            />
            <Heading fontSize="3xl" fontWeight="medium">
              AI interviewer set up
            </Heading>
          </Flex>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onNext();
            }}
            style={{ width: "100%" }}
          >
            <VStack spacing={6} align="stretch" w="100%">
              <FormControl
                error={errors.name}
                displayName="Name your AI interviewer"
              >
                <Input
                  {...register("name", {
                    required: "Name is required",
                    validate: (value) =>
                      value.trim().length > 0 || "Name cannot be empty",
                  })}
                  placeholder="Will not be displayed to candidates"
                  _placeholder={{ color: "textPlaceholderDefault" }}
                  fontSize="sm"
                  borderRadius="md"
                  w="100%"
                />
              </FormControl>
              <FormControl
                error={errors.displayName}
                displayName="What job title should the AI interviewer use?"
              >
                <Input
                  {...register("displayName", {
                    required: "Job title is required",
                    validate: (value) =>
                      value.trim().length > 0 || "Job title cannot be empty",
                  })}
                  placeholder="Will be displayed to candidates"
                  _placeholder={{ color: "textPlaceholderDefault" }}
                  fontSize="sm"
                  borderRadius="md"
                  w="100%"
                />
              </FormControl>
              {isPositionRequired && (
                <PositionField
                  control={control}
                  errors={errors}
                  isRequired={isPositionRequired}
                  theme={theme}
                  setSelectedPosition={setSelectedPosition}
                />
              )}
            </VStack>
            <Flex w="100%" justify="space-between" mt={8}>
              <Button variant="ghost" type="button" onClick={onCancel}>
                Cancel
              </Button>
              <Button colorScheme="blue" type="submit">
                Next: Job description
              </Button>
            </Flex>
          </form>
        </Flex>
      </Box>
    </FormProvider>
  );
};
